import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import logoSrc from '@/assets/logos/radium-logo-text.svg';
import { Button } from '@/components/interactive/Button';

interface MobileDrawerProps {
  onClose: () => void;
  items: { to: string; label: string }[];
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MobileDrawer({ onClose, items }: MobileDrawerProps) {
  const labelId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Lock body scroll while open; remember the previously-focused element so
  // we can restore focus on close (where keyboard users came from).
  useEffect(() => {
    previouslyFocused.current =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the drawer on open (first focusable, typically the
    // close button).
    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, []);

  // Escape closes; Tab cycles focus inside the drawer.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      onClick={(e) => {
        // Backdrop click closes (only when the click hits the dialog
        // background itself, not inner content).
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(10, 11, 14, 0.96)',
        backdropFilter: 'blur(20px)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h2
        id={labelId}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Site navigation
      </h2>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 64,
        }}
      >
        <Link to="/" onClick={onClose} aria-label="Radium home">
          <img
            src={logoSrc}
            alt="Radium"
            style={{ height: 28, width: 'auto' }}
          />
        </Link>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          style={{ padding: 8, color: 'var(--ink-primary)' }}
        >
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <nav
        aria-label="Primary"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          marginTop: 32,
        }}
      >
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            onClick={onClose}
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 36,
              fontWeight: 600,
              color: 'var(--ink-primary)',
              letterSpacing: '-0.01em',
              textDecoration: 'none',
            }}
          >
            {it.label}
          </Link>
        ))}
      </nav>
      <div style={{ marginTop: 'auto' }}>
        <Button href="/contact" variant="primary" size="lg" chevron>
          Contact us
        </Button>
      </div>
    </div>
  );
}
